<template>
    <div
        class="question-row question-row-input"
        :data-question-to="props.content.next_question?.value"
        :data-linked-form-input="props.content.linked_form_input?.value"
        :data-lead-service-name="name"
    >
        <transition name="floating-label">
            <p
                v-if="hasFloatingLabel && fieldValue !== ''"
                class="input__floating-label"
            >
                {{ props.content.placeholder?.value }}
            </p>
        </transition>

        <input
            :id="props.content.name"
            ref="inputElement"
            :value="fieldValue"
            form="responseForm"
            class="question-answer"
            data-input-type="ExtraData"
            :type="type"
            :name="props.content.name"
            :placeholder="props.content.placeholder?.value"
            :data-mask="props.content.mask.value"
            :data-validation="props.content.validation?.value"
            :class="(hasFloatingLabel && fieldValue !== '') && 'input--floating-label'"
            @input="set"
            @click="handleResetValidation"
        >
    </div>

    <div class="question-row question-row-button">
        <button
            class="question-button-next button"
            type="button"
            value="next"
            data-showstopper="none"
            data-question-to="next_question"
            @click="check"
        >
            <zoe-atom-text
                :item="props.content.button_label.value"
            />
        </button>
    </div>
</template>

<script setup>
import { onBeforeMount, onMounted, computed, ref } from 'vue';
import { useStore } from 'vuex';

import { addExtraData, getQuestionName, questionProps, storeExtraData } from '@mixins/quiz/v2.0.0/question';
import { checkCompleted } from '@mixins/quiz/v2.0.0/checks';
import { getNextQuestion } from '@mixins/quiz/v2.0.0/get-next-question';
import { useMask } from '../../../../../js/vue-flows/default-components/sb-mask/v2/mask';
import { setInvalid } from '@mixins/quiz/v2.0.0/invalid';
import { validate } from '../../../../../js/vue-flows/default-components/sb-validate/v2/validate';
import getCurrentStep from '@mixins/utils/get-current-step';

import ZoeAtomText from '../../../../atoms/text/v1.0.0/zoe-atom-text.vue';
import stripHtml from '../../../../../mixins/utils/strip-html';

const store = useStore();
const props = defineProps(questionProps);

const name = getQuestionName(props);
const uuid = ref(props.content?._info?.uuid);

const inputElement = ref(null);

onBeforeMount(() => {
    addExtraData(name, {
        name,
        value: '',
        mask: props.content.mask?.value,
        validator: props.content.validation?.value,
        uuid: uuid.value,
        question: stripHtml(props.content.text.value),
        sendData: props.content.send_to_lead_service?.value !== false,
    });
});

onMounted(() => {
    useMask('quiz', inputElement.value, name);
});

const fieldValue = computed(() => {
    return store.state.quiz.extradata[name]?.value || '';
});

const type = computed(() => {
    const validation = props.content.validation?.value;
    let type = 'text';

    if (validation === 'email') {
        type = 'email';
    }

    if (validation === 'telephone' || validation === 'mobile' || validation === 'telephoneLibphonenumber') {
        type = 'tel';
    }

    return type;
});

const currentStep = computed(() => {
    return getCurrentStep();
});

const hasFloatingLabel = computed(() => {
    return store.getters['styling/getInputForPlacement'](currentStep.value.key, 'floatingLabel');
});

const set = event => {
    const maskedValue = useMask('quiz', event.target, name);
    event.target.value = maskedValue;

    storeExtraData(name, 'value', maskedValue);
    storeExtraData(name, 'label', maskedValue);
};

const handleResetValidation = () => {
    if (store.getters['form/getFieldData']('preValidate', name) === false) {
        store.commit('form/setFieldData', { name, key: 'preValidate', value: null });
        store.commit('form/setFieldData', { name, key: 'validate', value: null });
    }
};

const check = event => {
    const questionWrapper = event.target.closest('.question');
    const input = questionWrapper.querySelector('input, select');
    const inputContainer = questionWrapper.querySelector('.question-row');
    const buttons = questionWrapper.querySelectorAll('.button');
    const delay = questionWrapper.dataset.delay ? parseInt(questionWrapper.dataset.delay) : 0;

    storeExtraData(name, 'value', input.value);
    storeExtraData(name, 'label', input.value);

    if (!validate('quiz', name, inputElement.value)) {
        setInvalid(inputContainer);
        return;
    }

    const answerValue = input.value;
    const linkedFormInputIdentifier = inputContainer.dataset.linkedFormInput;
    const worktypeId = '';
    const enquiryDashboardRedirectVersion = '';
    const nextQuestion = getNextQuestion(inputContainer.dataset.questionTo, store.state.quiz.currentQuestion);

    checkCompleted(name, buttons, props.preHook, nextQuestion, props.afterHook, answerValue, linkedFormInputIdentifier, worktypeId, enquiryDashboardRedirectVersion, delay);
};
</script>

<style lang="scss">
@use 'zoe-quiz-textfield';
</style>
